/**
 * ReasonPieChart — Alarms by Reason pie chart (Chart.js).
 */

import { useRef, useEffect, useState } from 'react';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

const REASON_TOOLTIP = [
    'Shows total nurse-call events broken down by the recorded reason.',
    'Only events where a reason was selected are included. Click a reason',
    'in the legend to show or hide it on the chart.',
];

/* ── ReasonInfoTip — ⓘ icon with hover popover ── */
const ReasonInfoTip = () => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const [style, setStyle] = useState({});

    const handleEnter = () => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const spaceRight = window.innerWidth - rect.left;
        const spaceBelow = window.innerHeight - rect.bottom;

        setStyle({
            position: 'fixed',
            left: spaceRight < 320 ? undefined : `${rect.left}px`,
            right: spaceRight < 320 ? '16px' : undefined,
            top: spaceBelow < 80 ? undefined : `${rect.bottom + 6}px`,
            bottom: spaceBelow < 80 ? `${window.innerHeight - rect.top + 6}px` : undefined,
            maxWidth: '300px',
            whiteSpace: 'normal',
        });
        setOpen(true);
    };

    return (
        <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)} style={{ cursor: 'help', marginLeft: '6px', color: 'var(--color-text-muted)' }}>
            <i className="fe fe-info" style={{ fontSize: 'var(--font-size-sm)' }} />
            {open && (
                <span className="na-type-popover" style={style}>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)' }}>About This Chart</span>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>{REASON_TOOLTIP.join(' ')}</span>
                </span>
            )}
        </span>
    );
};

const COLORS = [
    '#2C7BE5', '#392CE5', '#05B2FF', '#A6C5F7', '#8989FF', '#D2DDEC',
    '#E52C7B', '#962CE5', '#E52CD8', '#E52C7B', '#E5392C', '#09BA00',
    '#0CFF00', '#8AFF85', '#AEFF00', '#F2FF00', '#A7B000',
];
const OTHER_COLOR = '#9CA3AF';
const MAX_VISIBLE_REASONS = 10;
const LEGEND_BOTTOM_BREAKPOINT = 768;

/** Top-N + Other aggregation. Returns { labels, values, colors, otherBreakdown }. */
const aggregateReasons = (labels, values) => {
    const pairs = labels.map((label, i) => ({ label, value: values[i] || 0 }));
    pairs.sort((a, b) => b.value - a.value);

    if (pairs.length <= MAX_VISIBLE_REASONS) {
        return {
            labels: pairs.map((p) => p.label),
            values: pairs.map((p) => p.value),
            colors: pairs.map((_, i) => COLORS[i % COLORS.length]),
            otherBreakdown: null,
        };
    }

    const top = pairs.slice(0, MAX_VISIBLE_REASONS);
    const rest = pairs.slice(MAX_VISIBLE_REASONS);
    const otherTotal = rest.reduce((s, p) => s + p.value, 0);

    return {
        labels: [...top.map((p) => p.label), `Additional... (${rest.length})`],
        values: [...top.map((p) => p.value), otherTotal],
        colors: [...top.map((_, i) => COLORS[i % COLORS.length]), OTHER_COLOR],
        otherBreakdown: rest,
    };
};

const ReasonPieChart = ({ reasonData }) => {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);
    const otherBreakdownRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current) return;
        const rawLabels = reasonData?.labels || [];
        const rawValues = reasonData?.totals || [];

        if (rawLabels.length === 0) {
            if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
            return;
        }

        const { labels, values, colors, otherBreakdown } = aggregateReasons(rawLabels, rawValues);
        otherBreakdownRef.current = otherBreakdown;

        const getLegendPosition = () => (window.innerWidth < LEGEND_BOTTOM_BREAKPOINT ? 'bottom' : 'left');

        if (chartRef.current) {
            chartRef.current.data.labels = labels;
            chartRef.current.data.datasets[0].data = values;
            chartRef.current.data.datasets[0].backgroundColor = colors;
            chartRef.current.options.plugins.legend.position = getLegendPosition();
            chartRef.current.update();
        } else {
            chartRef.current = new Chart(canvasRef.current, {
                type: 'pie',
                data: {
                    labels,
                    datasets: [{
                        data: values,
                        backgroundColor: colors,
                    }],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: true, position: getLegendPosition() },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => {
                                    const total = ctx.dataset.data.reduce((s, v) => s + (v || 0), 0);
                                    const pct = total > 0 ? ((ctx.raw / total) * 100).toFixed(1) : 0;
                                    return ` ${ctx.label}: ${ctx.formattedValue} (${pct}%)`;
                                },
                                afterBody: (items) => {
                                    const breakdown = otherBreakdownRef.current;
                                    if (!breakdown) return undefined;
                                    const isOther = items.some((it) => typeof it.label === 'string' && it.label.startsWith('Additional.'));
                                    if (!isOther) return undefined;
                                    return ['', ...breakdown.map((p) => `• ${p.label}: ${p.value}`)];
                                },
                            },
                        },
                    },
                },
            });
        }

        const handleResize = () => {
            if (!chartRef.current) return;
            const next = getLegendPosition();
            if (chartRef.current.options.plugins.legend.position !== next) {
                chartRef.current.options.plugins.legend.position = next;
                chartRef.current.update();
            }
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
        };
    }, [reasonData]);

    const hasData = reasonData?.labels?.length > 0;

    return (
        <div className="md-section">
            <h3 className="md-section__title" style={{ marginBottom: 'var(--space-4)' }}>Alarms by Reason<ReasonInfoTip /></h3>
            {hasData ? (
                <div className="md-chart-container md-chart-container--pie">
                    <canvas ref={canvasRef} />
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--color-text-muted)' }}>No reason data available</div>
            )}
        </div>
    );
};

export default ReasonPieChart;
